import './index.less';

import classNames from 'classnames';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactSortable, Sortable } from 'react-sortablejs';
import { AddIcon, DeleteIcon, DragMoveIcon } from 'tdesign-icons-react';
import { Button, Form } from 'tdesign-react';

import styles from './index.module.less';
import ItemComp from './Item';

const { FormItem } = Form;

const random = () => `array_${dayjs().valueOf()}`;

export interface IQA {
  q: string;
  a: string
}

export const CustomArrayQA: React.FC<
{
  value: IQA[];
  onChange: (value: IQA[]) => void;
  drag?: boolean;
  buttonSize?: 'small' | 'normal',
  disabled?: boolean
}
> = (props) => {
  const {
    drag = false,
    value = [],
    onChange,
    buttonSize = 'normal',
    disabled = false,
  } = props;

  const defaultValue: IQA[] = [{ q: '', a: '' }];
  const ref = useRef<any[]>([]);

  const [compValue, setCompValue] = useState<IQA[]>(defaultValue);

  const [keys, setKeys] = useState([defaultValue]?.map(() => random()));
  const [tempKeys, setTempKeys] = useState([defaultValue]?.map(() => random()));

  const [changeKeyIndex, setChangeKeyIndex] = useState<number>(-1);

  useEffect(() => {
    if (!isEmpty(value)) {
      setCompValue([...value]);
      ref.current = [...value];
      setArrayKeys();
    }
  }, [value]);

  useEffect(() => {
    setTempKeys(keys);
  }, [keys]);

  const setArrayKeys = () => {
    if (keys?.length !== value?.length) {
      const newKeys = [...value]?.map((_e, i) => {
        if (i === changeKeyIndex) {
          return `${random()}_${i}`;
        }
        return tempKeys?.[i] || `${random()}_${i}`;
      });
      setKeys(newKeys);
    }
  };

  const changeArrayInfoObject = useCallback((key: number, value: IQA) => {
    const temp = [...ref.current];
    temp[key] = {
      ...temp[key],
      ...value,
    };
    onChange([...temp]);
  }, [compValue]);

  const handleChange = useCallback((value: IQA, index: number) => {
    changeArrayInfoObject(index, value);
  }, [changeArrayInfoObject]);

  const removeItem = (index: number, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    const newArray = [...compValue];

    newArray.splice(index, 1);
    onChange([...newArray]);
    const tempKeys = [...keys];
    tempKeys.splice(index, 1);
    setKeys(tempKeys);
    if (newArray?.length === 0) {
      setCompValue([]);
    }
  };

  const addItem = (index: number, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    const aArray = [...compValue];
    aArray.splice(index + 1, 0, ...defaultValue);
    onChange(aArray);

    const tempKeys = [...keys];
    tempKeys.splice(index + 1, 0, random());
    setChangeKeyIndex(index + 1);
    setTempKeys(tempKeys);
  };

  const onEnd = (evt: Sortable.SortableEvent) => {
    const qIndex = evt.oldIndex || 0;
    const aIndex = evt.newIndex || 0;
    sortKey(qIndex, aIndex);
    sortData(qIndex, aIndex);
  };

  const sortData = (qIndex: number, aIndex: number) => {
    const copyValue = [...compValue];
    const copyValue2 = [...compValue];
    copyValue.splice(qIndex, 1);
    copyValue.splice(aIndex, 0, copyValue2[qIndex]);
    onChange(copyValue);
  };

  const sortKey = (qIndex: number, aIndex: number) => {
    const copyValue = [...tempKeys];
    const copyValue2 = [...tempKeys];
    copyValue.splice(qIndex, 1);
    copyValue.splice(aIndex, 0, copyValue2[qIndex]);

    setKeys(copyValue);
  };

  return (
    <div className={styles.arrayWrap}>
      <ReactSortable
        disabled={!drag}
        handle={'.draggable'}
        list={compValue.map((x: any) => ({ ...x }))}
        onEnd={onEnd}
        setList={() => { }}
      >
        {compValue.map(({ }, index) => (
          <div className="custom-form-group" style={{ alignItems: 'flex-end' }} key={`${keys?.[index]}`}>
            <div className={styles.group} style={{ flexGrow: '1' }}>
              <ItemComp value={{ ...ref.current[index] }}
                disabled={disabled} onChange={(val) => {
                  handleChange(val, index);
                }} />
            </div>
            <FormItem className={styles.options}>
              <Button
                className={styles.delete}
                theme="default"
                disabled={compValue.length === 1 || disabled}
                size={buttonSize === 'small' ? 'small' : 'medium'}
                onClick={event => removeItem(index, event)}
                icon={<DeleteIcon />}
                style={{ padding: buttonSize === 'small' ? '0 9px' : undefined }}
              >{buttonSize === 'normal' && '删除'}</Button>
              <Button
                className={styles.add}
                theme="default"
                disabled={disabled}
                size={buttonSize === 'small' ? 'small' : 'medium'}
                onClick={event => addItem(index, event)}
                style={{ padding: buttonSize === 'small' ? '0 8px' : undefined }}
                icon={<AddIcon />}
              >{buttonSize === 'normal' && '增加'}</Button>
              {drag && <Button
                className={classNames('draggable', styles.drag)}
                theme="default"
                size={buttonSize === 'small' ? 'small' : 'medium'}
                icon={<DragMoveIcon />}
              ></Button>}
            </FormItem>
          </div>
        ))}
      </ReactSortable>
    </div>
  );
};
